import React from 'react';
import { Loader2, FileText } from 'lucide-react';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { useToast } from './Toast';
import { useActiveOcrJobs, type ActiveJob } from '../lib/useActiveOcrJobs';
import { shouldReloadAfterDrain } from '../lib/ocrDrain';

function phaseLabel(status: ActiveJob['status']): string {
  if (status === 'pending') return tr('queued', '排隊中', '排队中');
  if (status === 'importing') return tr('building statements', '建立報表中', '建立报表中');
  return tr('reading pages', '讀取頁面中', '读取页面中');
}

/** Compress page numbers into ranges: [7,8,12,13,14] → "7–8, 12–14". */
export function formatPageRanges(pages: number[]): string {
  if (!pages.length) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) { end = sorted[i]; continue; }
    parts.push(start === end ? `${start}` : `${start}–${end}`);
    start = end = sorted[i];
  }
  parts.push(start === end ? `${start}` : `${start}–${end}`);
  return parts.join(', ');
}

function elapsed(startedAt: string): string {
  if (!startedAt) return '';
  const iso = startedAt.includes('T') ? startedAt : `${startedAt.replace(' ', 'T')}Z`;
  const start = Date.parse(iso);
  if (Number.isNaN(start)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

export default function OcrTray({ jobs: jobsProp }: { jobs?: ActiveJob[] }) {
  // Layout owns the poll and passes jobs down (it also locks "More Uploads"
  // on the same data); standalone consumers fall back to their own poll.
  const hookJobs = useActiveOcrJobs();
  const jobs = jobsProp ?? hookJobs.jobs;
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const prevRef = React.useRef<ActiveJob[]>([]);
  const prevCountRef = React.useRef(0);
  const [, tick] = React.useState(0);

  // Advance the elapsed-time labels once a second.
  React.useEffect(() => {
    const id = setInterval(() => tick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Refresh the page once the WHOLE queue drains (background OCR can take
  // minutes and every list/tree is stale afterwards). A pending type-mismatch
  // warning suppresses the reload so the user's choice is not lost.
  React.useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = jobs.length;
    if (!shouldReloadAfterDrain(prev, jobs.length, false)) return;
    const timer = setTimeout(async () => {
      let mismatchPending = false;
      try {
        const res = await api('/file-storage/type-mismatches') as { data?: unknown[] };
        mismatchPending = (res?.data?.length ?? 0) > 0;
      } catch { /* transient poll failure — reload is still the best refresh */ }
      if (shouldReloadAfterDrain(prev, jobs.length, mismatchPending)) {
        window.location.reload();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [jobs.length]);

  // Toast when a job leaves the list (active-jobs only lists in-flight rows).
  React.useEffect(() => {
    const prev = prevRef.current;
    const currentIds = new Set(jobs.map(j => j.id));
    const finished = prev.filter(j => !currentIds.has(j.id));
    prevRef.current = jobs;
    for (const job of finished) {
      api(`/file-storage/${job.id}`).then((row: any) => {
        // Debug: surface the OCR text + the parse prompt/output in the browser
        // console (the queued parse runs server-side, so the page never sees it).
        if (row?.ocr_text) {
          console.log(`==== OCR TEXT [${row.ocr_source || 'unknown'}] ====`);
          console.log(row.ocr_text);
        }
        if (row?.ocr_debug_prompt || row?.ocr_debug_raw) {
          console.log('==== LLM PROMPT ====');
          console.log(row.ocr_debug_prompt || '(none)');
          console.log('==== LLM OUTPUT ====');
          console.log(row.ocr_debug_raw || '(none)');
        }
        const status = row?.ocr_status;
        if (status === 'failed' || status === 'unclear') {
          toast.error(tr(`${job.filename} could not be read`, `${job.filename} 無法讀取`, `${job.filename} 无法读取`));
        } else if (job.statements_ready > 0) {
          toast.success(tr(
            `${job.filename} — ${job.statements_ready} statements ready`,
            `${job.filename} — ${job.statements_ready} 份報表已就緒`,
            `${job.filename} — ${job.statements_ready} 份报表已就绪`,
          ));
        } else {
          toast.success(tr(`${job.filename} processed`, `${job.filename} 已處理`, `${job.filename} 已处理`));
        }
      }).catch(() => { /* non-critical */ });
    }
  }, [jobs, toast]);

  if (jobs.length === 0) return null;

  const pagesTotal = jobs.reduce((n, j) => n + (j.pages_total || 0), 0);
  const pagesDone = jobs.reduce((n, j) => n + (j.pages_done || 0), 0);
  const statementsReady = jobs.reduce((n, j) => n + (j.statements_ready || 0), 0);
  const blocksPending = jobs.reduce((n, j) => n + (j.blocks_pending || 0), 0);
  const readingCount = jobs.reduce((n, j) => n + (j.reading_pages?.length || 0), 0);
  const single = jobs.length === 1 ? jobs[0] : null;
  // "Importing" = OCR pages are all done and months are being written. Don't
  // show "17 of 17 pages" there — show the import progress instead.
  const importing = single ? single.status === 'importing' : jobs.every(j => j.status === 'importing');
  const title = single ? single.filename : `${jobs.length} ${tr('files', '個檔案', '个文件')}`;
  const readingList = single ? formatPageRanges(single.reading_pages || []) : '';

  return (
    <div className="relative">
      <button
        data-testid="ocr-tray"
        onClick={() => setOpen(o => !o)}
        title={tr('OCR activity', 'OCR 活動', 'OCR 活动')}
        className="flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm shadow-sm hover:bg-muted"
      >
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-600" />
        <span className="flex flex-col items-start text-left leading-tight">
          <span className="text-xs font-semibold">
            {tr('OCR', 'OCR', 'OCR')} · {title}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {importing
              ? tr('building statements…', '建立報表中…', '建立报表中…')
              : single && readingList
                ? tr(`reading pages ${readingList}`, `正在讀取第 ${readingList} 頁`, `正在读取第 ${readingList} 页`)
                : readingCount > 0
                  ? tr(`${readingCount} pages reading`, `${readingCount} 頁讀取中`, `${readingCount} 页读取中`)
                  : pagesTotal > 0 && pagesDone === 0
                    ? tr('preparing document…', '準備文件中…', '准备文件中…')
                    : phaseLabel(single?.status ?? jobs[0].status)}
            {' · '}
            {importing
              ? tr(
                  `${statementsReady} statements ready${blocksPending > 0 ? ` · ${blocksPending} blocks left` : ''}`,
                  `${statementsReady} 份報表已就緒${blocksPending > 0 ? ` · 尚餘 ${blocksPending} 組` : ''}`,
                  `${statementsReady} 份报表已就绪${blocksPending > 0 ? ` · 尚余 ${blocksPending} 组` : ''}`,
                )
              : tr(`${pagesDone} of ${pagesTotal} pages`, `${pagesDone} / ${pagesTotal} 頁`, `${pagesDone} / ${pagesTotal} 页`)}
          </span>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 space-y-2 rounded-md border bg-card p-2 shadow-lg">
          {jobs.map(job => {
            const pct = job.pages_total > 0 ? Math.round((job.pages_done / job.pages_total) * 100) : null;
            const reading = formatPageRanges(job.reading_pages || []);
            // Page OCR is done but statements are still being built — a solid
            // 100% bar reads as "finished". Show an indeterminate bar instead
            // (the label above already says "building statements…").
            const building = job.status === 'importing';
            return (
              <div key={job.id} className="rounded-md border p-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm font-medium">{job.filename}</span>
                  <span className="text-xs text-muted-foreground">{elapsed(job.started_at)}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {job.status === 'importing'
                    ? tr('building statements…', '建立報表中…', '建立报表中…')
                    : <>{phaseLabel(job.status)}{job.pages_total > 0 && <> · {tr(`${job.pages_done} of ${job.pages_total} pages`, `${job.pages_done} / ${job.pages_total} 頁`, `${job.pages_done} / ${job.pages_total} 页`)}</>}</>}
                </div>
                {reading && (
                  <div className="mt-0.5 text-xs text-blue-600">
                    {tr(`reading pages ${reading}`, `正在讀取第 ${reading} 頁`, `正在读取第 ${reading} 页`)}
                  </div>
                )}
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${building ? 'bg-blue-400 animate-pulse' : `bg-blue-500 ${pct === null ? 'animate-pulse' : ''}`}`}
                    style={{ width: building || pct === null ? '100%' : `${pct}%` }}
                  />
                </div>
                {job.statements_ready > 0 && (
                  <div className="mt-1 text-xs text-green-900 dark:text-green-200">
                    {job.blocks_pending > 0
                      ? tr(
                          `${job.statements_ready} statements ready · ${job.blocks_pending} blocks left`,
                          `${job.statements_ready} 份報表已就緒 · 尚餘 ${job.blocks_pending} 組`,
                          `${job.statements_ready} 份报表已就绪 · 尚余 ${job.blocks_pending} 组`,
                        )
                      : tr(`${job.statements_ready} statements ready`, `${job.statements_ready} 份報表已就緒`, `${job.statements_ready} 份报表已就绪`)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
